import React, { useMemo, useState } from "react"

import ConfirmationModal from "~/library/ConfirmationModal"

import getAppConfig from "~/utils/getAppConfig"
import { useToast } from "~/utils/Toast"
import { apiPayments, apiSavedPaymentMethods } from "~/routes"

import CatchCard from "~/library/CatchCard"
import SavePaymentMethodModal from "~/components/payments/SavePaymentMethodModal"
import PayablePaymentModal from "~/components/payments/PayablePaymentModal"
import PaymentsSection from "~/components/payments/PaymentsSection"
import SavedPaymentMethodsCard from "~/components/payments/SavedPaymentMethodsCard"

import type Payment from "~/types/Payment"
import type PaymentItem from "~/types/PaymentItem"
import type SavedPaymentMethod from "~/types/SavedPaymentMethod"
import type { PayablePaymentResource } from "~/types/PayablePaymentResource"

interface PaymentOwnerDetails {
  name?: string
  email?: string
  phone?: string
}

interface Props {
  resource: PayablePaymentResource
  payments?: Payment[]
  initialPaymentItems?: PaymentItem[]
  paymentOwner?: PaymentOwnerDetails | null
  stripePublishableKey: string
  onRefresh: () => Promise<void> | void
  totalAmountCents?: number | null
}

const PayablePaymentsSection: React.FC<Props> = ({
  resource,
  payments = [],
  initialPaymentItems = [],
  paymentOwner = null,
  stripePublishableKey,
  onRefresh,
  totalAmountCents = null,
}) => {
  const toast = useToast()
  const { venueSlug } = getAppConfig()

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false)
  const [savedCardsReloadKey, setSavedCardsReloadKey] = useState(0)

  const [deletingPayment, setDeletingPayment] = useState<string | null>(null)
  const [sendingInvoice, setSendingInvoice] = useState<string | null>(null)
  const [confirmingInvoice, setConfirmingInvoice] = useState<string | null>(null)

  const paymentsList = useMemo(() => payments || [], [payments])
  const hasPaymentOwner = !!paymentOwner
  const currencyIso = resource.currency || "USD"

  const paidAmountCents = useMemo(() => {
    return paymentsList
      .filter((payment) => payment.status === "paid" || !!payment.paidOn)
      .reduce((sum, payment) => sum + (payment.amount?.cents || 0), 0)
  }, [paymentsList])

  const remainingAmountCents = useMemo(() => {
    if (totalAmountCents == null) return null
    return Math.max(totalAmountCents - paidAmountCents, 0)
  }, [totalAmountCents, paidAmountCents])

  const fullyPaid = totalAmountCents != null && remainingAmountCents === 0

  const handleRefresh = async () => {
    await onRefresh()
    setSavedCardsReloadKey((prev) => prev + 1)
  }

  const copyLinkToClipboard = async (link?: string | null) => {
    if (!link) {
      toast("No receipt or invoice link available", "warning")
      return
    }

    try {
      await navigator.clipboard.writeText(link)
      toast("Link copied", "success")
    } catch (_error) {
      toast("Failed to copy link", "danger")
    }
  }

  const setDefaultSavedCard = async (method: SavedPaymentMethod) => {
    try {
      await apiSavedPaymentMethods.setDefault({
        data: {
          venueSlug,
          payableType: resource.payableType,
          payableId: resource.payableId,
          id: method.id,
        },
      })

      toast("Default card updated", "success")
      await handleRefresh()
    } catch (res: any) {
      toast(res.body?.errors?.join(", ") || "Failed to update default card", "danger")
    }
  }

  const deleteSavedCard = async (method: SavedPaymentMethod) => {
    const confirmed = window.confirm(
      `Delete ${
        method.displayName || "this saved card"
      }? This card will no longer be available for future charges.`
    )

    if (!confirmed) return

    try {
      await apiSavedPaymentMethods.destroy({
        venueSlug,
        payableType: resource.payableType,
        payableId: resource.payableId,
        id: method.id,
      })

      toast("Saved card deleted", "success")
      await handleRefresh()
    } catch (res: any) {
      toast(res.body?.errors?.join(", ") || "Failed to delete saved card", "danger")
    }
  }

  const deletePayment = async () => {
    if (!deletingPayment) return

    try {
      await apiPayments.destroy({
        venueSlug,
        id: deletingPayment,
        payableId: resource.payableId,
        payableType: resource.payableType,
      })

      setDeletingPayment(null)
      toast("Payment removed", "success")
      await handleRefresh()
    } catch (res: any) {
      toast(res.body?.errors?.join(", ") || "Failed to remove payment", "danger")
    }
  }

  const sendInvoice = async () => {
    if (!confirmingInvoice) return

    setSendingInvoice(confirmingInvoice)
    setConfirmingInvoice(null)

    try {
      await apiPayments.sendInvoice({
        venueSlug,
        id: confirmingInvoice,
        payableId: resource.payableId,
        payableType: resource.payableType,
      })

      toast("Invoice sent successfully", "success")
      await handleRefresh()
    } catch (res: any) {
      toast(res.body?.errors?.join(", ") || "Failed to send invoice", "danger")
    } finally {
      setSendingInvoice(null)
    }
  }

  return (
    <>
      <div className="mb-3">
        <CatchCard>
          {!hasPaymentOwner ? (
            <div className="text-muted">
              Add a contact or client before using saved cards.
            </div>
          ) : (
            <SavedPaymentMethodsCard
              key={`${resource.payableType}-saved-cards-${resource.payableId}-${savedCardsReloadKey}`}
              payableType={resource.payableType}
              payableId={resource.payableId}
              title="Saved Payment Methods"
              onAddCard={() => setIsAddCardModalOpen(true)}
              onSetDefault={setDefaultSavedCard}
              onDelete={deleteSavedCard}
            />
          )}
        </CatchCard>
      </div>

      <PaymentsSection
        payments={paymentsList}
        currencyIso={currencyIso}
        totalAmountCents={totalAmountCents}
        paidAmountCents={paidAmountCents}
        remainingAmountCents={remainingAmountCents}
        fullyPaid={fullyPaid}
        sendingInvoice={sendingInvoice}
        onCreateInvoice={() => setIsPaymentModalOpen(true)}
        onCopyLink={copyLinkToClipboard}
        onSendInvoice={(paymentId) => setConfirmingInvoice(paymentId)}
        onDeletePayment={(paymentId) => setDeletingPayment(paymentId)}
      />

      <ConfirmationModal
        isOpen={!!deletingPayment}
        onClose={() => setDeletingPayment(null)}
        onConfirm={deletePayment}
        title="Delete Payment?"
      />

      <ConfirmationModal
        isOpen={!!confirmingInvoice}
        onClose={() => setConfirmingInvoice(null)}
        onConfirm={sendInvoice}
        title="Send Invoice?"
        description="Are you sure you want to send this invoice? This will email the invoice to the customer."
      />

      {isPaymentModalOpen && (
        <PayablePaymentModal
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={handleRefresh}
          resource={resource}
          initialItems={initialPaymentItems}
          totalAmountCents={totalAmountCents}
          paidAmountCents={paidAmountCents}
          remainingAmountCents={remainingAmountCents}
        />
      )}

      {isAddCardModalOpen && (
        <SavePaymentMethodModal
          payableType={resource.payableType}
          payableId={resource.payableId}
          stripePublishableKey={stripePublishableKey}
          paymentOwner={paymentOwner}
          onClose={() => setIsAddCardModalOpen(false)}
          onSuccess={handleRefresh}
        />
      )}
    </>
  )
}

export default PayablePaymentsSection
